// ============================================================================
// SearchIndex context — fuente única de verdad para la búsqueda GLOBAL.
//
// Carga lazy los listados de las entidades buscables (inventario, carta, mesas,
// pedidos, reservaciones, ventas) por sede activa, normaliza cada uno a un
// `SearchEntry` (módulo + título + sub-título + meta + ruta de navegación) y
// expone una función `search(q)` que filtra por substring (acento-insensible)
// rankeando primero por título y luego por sub-título.
//
// Los listados se cachean en memoria; cuando cambia la sede activa se
// re-fetchean. El cache no es real-time — para ese caso ya existe el sistema
// de eventos `useRealtime`. La búsqueda global vale como atajo de navegación.
//
// Re-spec 2-jun-2026 (chat usuario): "una búsqueda global que sea fácil y
// que desde esa búsqueda pueda acceder a lo que sea que necesito, pero con un
// indicador del módulo antes de clickear".
// ============================================================================
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useBranch } from './branch-context';
import { listInventario } from '../../api/inventario';
import { listMenu } from '../../api/menu';
import { listMesas } from '../../api/mesas';
import { listPedidos } from '../../api/pedidos';
import { listReservaciones } from '../../api/reservaciones';
import { fetchVentas } from '../../api/ventas';
import { listGastos, listProveedores } from '../../api/gastos';
import { listClientes } from '../../api/clientes';
import { formatMoney } from '@/lib/moneda';
import { textoDe } from '../../i18n/textos';
import { textosBusqueda } from '../../i18n/modulos/busqueda';

export type SearchModule =
  | 'inventory'
  | 'menu'
  | 'tables'
  | 'orders'
  | 'reservations'
  | 'billing'
  | 'purchases'
  | 'terceros';

export interface SearchEntry {
  /** Clave única (`modulo:id`). */
  id: string;
  module: SearchModule;
  /** Texto principal (el "qué"). Ej: "Pollo deshuesado". */
  title: string;
  /** Texto secundario (el contexto). Ej: "POL-PER · Carnes". */
  subtitle?: string;
  /** Texto a la derecha (precio, estado, total). */
  meta?: string;
  /** Heno pre-computado en lowercase + sin acentos para matching rápido. */
  haystack: string;
  /** Ruta de navegación al hacer click. */
  path: string;
}

interface SearchContextValue {
  entries: SearchEntry[];
  loading: boolean;
  /** Re-fetch manual (por si el usuario quiere refrescar). */
  refresh: () => void;
  /** Filtra y rankea — devuelve hasta `limit` resultados (default 30). */
  search: (q: string, limit?: number) => SearchEntry[];
}

const SearchContext = createContext<SearchContextValue | null>(null);

// Quita acentos para hacer matching insensible: "Pollo" matchea "póllo".
function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

// Helpers de UI específicos por módulo — labels claros para los badges
// (los íconos / colores se aplican en el componente GlobalSearch).
// Son CLAVES de textosBusqueda (i18n): se traducen en el render con
// useTextos(textosBusqueda) — ver global-search.tsx.
const MODULE_LABEL: Record<SearchModule, keyof typeof textosBusqueda.es> = {
  inventory:    'modInventory',
  menu:         'modMenu',
  tables:       'modTables',
  orders:       'modOrders',
  reservations: 'modReservations',
  billing:      'modBilling',
  purchases:    'modPurchases',
  terceros:     'modTerceros',
};

export const SEARCH_MODULE_LABEL = MODULE_LABEL;

// ----------------------------------------------------------------------------

export function SearchProvider({ children }: { children: ReactNode }) {
  const { currentBranchId } = useBranch();
  const [entries, setEntries] = useState<SearchEntry[]>([]);
  const [loading, setLoading] = useState(false);
  // Token incrementable para descartar respuestas viejas si el usuario cambia
  // de sede mientras se cargan datos.
  const loadToken = useRef(0);

  const load = useCallback(async () => {
    // La búsqueda global ahora es EN VIVO contra el backend (/api/search); este
    // índice client-side ya no se usa para buscar. Lo dejamos como no-op para no
    // fetchear 9 listas en cada cambio de sede.
    setEntries([]);
  }, []);

  // (legacy) Builder del índice client-side — conservado por referencia, sin uso.
  const loadLegacy = useCallback(async () => {
    if (!currentBranchId) {
      setEntries([]);
      return;
    }
    const token = ++loadToken.current;
    setLoading(true);

    // Cada fetch va en su propio `.catch(() => [])`: que falle uno no
    // debería tumbar la búsqueda global completa. La sede activa puede
    // no tener todos los datos (ej. una sede sin pedidos abiertos).
    const safe = <T,>(p: Promise<T>): Promise<T | null> =>
      p.then(v => v).catch(() => null);

    const [items, productos, mesas, pedidos, reservas, ventas, gastos, proveedores, clientes] = await Promise.all([
      safe(listInventario(currentBranchId)),
      safe(listMenu({ sede_id: currentBranchId })),
      safe(listMesas(currentBranchId)),
      safe(listPedidos({ sede_id: currentBranchId })),
      safe(listReservaciones({ sede_id: currentBranchId })),
      safe(fetchVentas({ sede_id: currentBranchId, limit: 2000 })),
      safe(listGastos({ sede_id: currentBranchId, limit: 2000 })),
      safe(listProveedores(currentBranchId)),
      safe(listClientes()),
    ]);

    // Si el usuario cambió de sede mientras cargábamos, descartamos.
    if (token !== loadToken.current) return;

    const out: SearchEntry[] = [];

    // ---- Inventario ----
    (items || []).forEach(it => {
      const sub = [it.codigo, it.categoria].filter(Boolean).join(' · ');
      out.push({
        id: `inventory:${it.id}`,
        module: 'inventory',
        title: it.nombre,
        subtitle: sub,
        meta: `${it.cantidad.toLocaleString('es-CO', { maximumFractionDigits: 2 })} ${it.unidad}`,
        haystack: normalize([it.nombre, it.codigo, it.categoria].filter(Boolean).join(' ')),
        path: `/inventory?item=${it.id}`,
      });
    });

    // ---- Carta ----
    (productos || []).forEach(p => {
      const sub = [p.codigo, p.subcategoria].filter(Boolean).join(' · ');
      out.push({
        id: `menu:${p.id}`,
        module: 'menu',
        title: p.nombre,
        subtitle: sub,
        meta: `${formatMoney(Number(p.precio || 0))}`,
        haystack: normalize([p.nombre, p.codigo, p.subcategoria, p.descripcion].filter(Boolean).join(' ')),
        path: `/menu?plato=${p.id}`,
      });
    });

    // ---- Mesas ----
    (mesas || []).forEach(m => {
      out.push({
        id: `tables:${m.id}`,
        module: 'tables',
        title: m.nombre,
        subtitle: `${m.capacidad} ${textoDe(textosBusqueda, 'persSep')} ${m.estado}${m.pedido_referencia ? ' · ' + m.pedido_referencia : ''}`,
        meta: m.pedido_total ? `${formatMoney(Number(m.pedido_total))}` : undefined,
        haystack: normalize([m.nombre, m.pedido_referencia, m.proxima_reserva_cliente].filter(Boolean).join(' ')),
        path: `/tables?mesa=${m.id}`,
      });
    });

    // ---- Pedidos ----
    (pedidos || []).forEach(p => {
      out.push({
        id: `orders:${p.id}`,
        module: 'orders',
        title: p.referencia,
        subtitle: [p.mesa_nombre, p.mesero_nombre, p.estado].filter(Boolean).join(' · '),
        meta: `${formatMoney(Number(p.total || 0))}`,
        haystack: normalize([p.referencia, p.mesa_nombre, p.mesero_nombre, p.estado, p.notas].filter(Boolean).join(' ')),
        path: `/orders?pedido_id=${p.id}`,
      });
    });

    // ---- Reservas ----
    (reservas || []).forEach(r => {
      out.push({
        id: `reservations:${r.id}`,
        module: 'reservations',
        title: r.cliente_nombre,
        subtitle: [r.fecha_hora?.slice(0, 16).replace('T', ' '), r.mesa_nombre, `${r.personas} ${textoDe(textosBusqueda, 'pers')}`, r.estado]
          .filter(Boolean).join(' · '),
        meta: r.cliente_telefono || undefined,
        haystack: normalize([r.cliente_nombre, r.cliente_telefono, r.mesa_nombre, r.motivo, r.notas].filter(Boolean).join(' ')),
        path: `/reservations?reserva=${r.id}`,
      });
    });

    // ---- Ventas / Facturas ----
    (ventas?.data || []).forEach(v => {
      const folio = `${v.prefijo || ''}${v.folio}`;
      out.push({
        id: `billing:${v.id}`,
        module: 'billing',
        title: folio,
        subtitle: [v.fecha_emision, v.cliente_nombre || v.razon_social, v.estado_dian].filter(Boolean).join(' · '),
        meta: `${formatMoney(Number(v.total || 0))}`,
        haystack: normalize([folio, v.cliente_nombre, v.cliente_identificacion, v.razon_social, v.cufe].filter(Boolean).join(' ')),
        path: `/billing?venta_id=${v.id}`,
      });
    });

    // ---- Costos y Gastos ----
    (gastos || []).forEach(g => {
      out.push({
        id: `purchases:${g.id}`,
        module: 'purchases',
        title: g.descripcion,
        subtitle: [g.categoria, g.proveedor, g.fecha].filter(Boolean).join(' · '),
        meta: `${formatMoney(Number(g.monto || 0))}`,
        haystack: normalize([g.descripcion, g.categoria, g.proveedor, g.factura].filter(Boolean).join(' ')),
        path: `/purchases`,
      });
    });

    // ---- Proveedores (Terceros) ----
    (proveedores || []).forEach(p => {
      out.push({
        id: `terceros:prov:${p.id}`,
        module: 'terceros',
        title: p.nombre,
        subtitle: [textoDe(textosBusqueda, 'proveedor'), p.nit ? `NIT ${p.nit}` : null, p.telefono].filter(Boolean).join(' · '),
        meta: p.email || undefined,
        haystack: normalize([p.nombre, p.nit, p.telefono, p.email].filter(Boolean).join(' ')),
        path: `/terceros`,
      });
    });

    // ---- Clientes (Terceros) ----
    (clientes?.clientes || []).forEach(c => {
      out.push({
        id: `terceros:cli:${c.identificacion}`,
        module: 'terceros',
        title: c.nombre,
        subtitle: [textoDe(textosBusqueda, 'cliente'), c.identificacion ? `Doc ${c.identificacion}` : null, c.telefono].filter(Boolean).join(' · '),
        meta: c.email || undefined,
        haystack: normalize([c.nombre, c.identificacion, c.telefono, c.email].filter(Boolean).join(' ')),
        path: `/terceros`,
      });
    });

    setEntries(out);
    setLoading(false);
  }, [currentBranchId]);

  useEffect(() => { load(); }, [load]);

  const search = useCallback((q: string, limit = 30): SearchEntry[] => {
    const needle = normalize(q.trim());
    if (!needle) return [];
    const tokens = needle.split(/\s+/).filter(Boolean);

    // Ranking: prefix-match en el título pesa más que substring en el haystack.
    const scored: { e: SearchEntry; score: number }[] = [];
    for (const e of entries) {
      let score = 0;
      const titleNorm = normalize(e.title);
      for (const t of tokens) {
        if (titleNorm.startsWith(t)) score += 10;
        else if (titleNorm.includes(t)) score += 6;
        else if (e.haystack.includes(t)) score += 3;
        else { score = -1; break; }
      }
      if (score > 0) scored.push({ e, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit).map(s => s.e);
  }, [entries]);

  const value = useMemo<SearchContextValue>(() => ({
    entries,
    loading,
    refresh: load,
    search,
  }), [entries, loading, load, search]);

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

export function useSearch(): SearchContextValue {
  const v = useContext(SearchContext);
  if (!v) throw new Error('useSearch debe usarse dentro de <SearchProvider>');
  return v;
}
